'use client';

import React from 'react';

/**
 * Display / intent modifiers: maps to `btn-<name>` in `src/styles/ui/_actions.scss`
 * (semantic, outline, link, and layout helpers — not physical size; use **`size`** for that).
 */
export type ButtonVariant =
  | 'primary'
  | 'secondary'
  | 'tertiary'
  | 'ghost'
  | 'warning'
  | 'danger'
  | 'outline'
  | 'outline-white'
  | 'outline-primary'
  | 'outline-secondary'
  | 'outline-tertiary'
  | 'outline-warning'
  | 'outline-danger'
  | 'link'
  | 'block'
  | 'center'
  | 'round'
  | 'icon'
  | 'rectangular'
  | 'close';

/** Physical control size → `btn-xs` … `btn-xlg` in `_actions.scss`. */
export type ButtonSize = 'xs' | 'sm' | 'md' | 'lg' | 'xlg';

export type ButtonBaseProps = {
  label?: React.ReactNode;
  className?: string;
  /** Adds `btn-${variant}` after the base `btn` class (e.g. `tertiary` → `btn-tertiary`). */
  variant?: ButtonVariant;
  /** Adds `btn-${size}` (e.g. `sm` → `btn-sm`, `xlg` → `btn-xlg`). Separate from **`variant`** so display vs scale stay independent. */
  size?: ButtonSize;
  icon?: React.ReactNode;
  iconPosition?: 'left' | 'right';
};

/**
 * Props for rendering the component as a native HTML <button> element.
 *
 * Combines the component's ButtonBaseProps with React's standard
 * ButtonHTMLAttributes<HTMLButtonElement>, while omitting the native
 * 'type' attribute so it can be redefined with a stricter union.
 *
 * Key fields:
 * - as?: 'button' — Indicates this prop set is intended for a native button element.
 * - href?: undefined — Explicitly disallowed when rendering a <button> (use an anchor variant for links).
 * - type?: 'button' | 'submit' | 'reset' — Restricts the HTML button type to the common options.
 *
 * This type forwards native button attributes (e.g. onClick, disabled, aria-*),
 * but prevents accidental use of link-specific props and conflicting 'type' definitions.
 *
 * @remarks
 * The underlying React.ButtonHTMLAttributes<HTMLButtonElement> is Omit'ed for 'type'
 * to avoid a conflicting or overly permissive 'type' signature; the redefined union
 * ensures predictable button behavior.
 *
 * @example
 * // Usage:
 * // <Button as="button" type="submit" onClick={handleSubmit}>Submit</Button>
 */
export type ButtonAsButton = ButtonBaseProps &
  Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'type' | keyof ButtonBaseProps> & {
    as?: 'button';
    href?: undefined;
    type?: 'button' | 'submit' | 'reset';
  };
export type ButtonAsAnchor = ButtonBaseProps &
  Omit<React.AnchorHTMLAttributes<HTMLAnchorElement>, keyof ButtonBaseProps> & {
    as: 'a';
    href: string;
    disabled?: boolean;
  };

export type ButtonProps = ButtonAsButton | ButtonAsAnchor;

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>((props, ref) => {
  const {
    label,
    className = '',
    variant,
    size,
    icon,
    iconPosition = 'left',
    as = 'button',
    href,
    disabled = false,
    ...rest
  } = props;
  const userClass =
    as === 'a' && className
      ? className
          .trim()
          .split(/\s+/)
          .filter((c) => c !== 'btn-link')
          .join(' ')
      : className;
  const linkClass = as === 'a' ? ' btn-link' : '';
  const variantClass = variant ? ` btn-${variant}` : '';
  const sizeClass = size ? ` btn-${size}` : '';
  const classes = `btn${linkClass}${variantClass}${sizeClass}${userClass ? ` ${userClass}` : ''}`;
  const hasLabel =
    label !== undefined &&
    ((typeof label === 'string' && label.trim() !== '') || typeof label !== 'string');
  const content = (
    <>
      {icon && iconPosition === 'left' && (
        <span className='iconify' aria-hidden='true'>
          {icon}
        </span>
      )}
      {hasLabel && <span className='label'>{label}</span>}
      {icon && iconPosition === 'right' && (
        <span className='iconify' aria-hidden='true'>
          {icon}
        </span>
      )}
    </>
  );

  if (as === 'a' && href) {
    const anchorAll = rest as React.AnchorHTMLAttributes<HTMLAnchorElement> & {
      disabled?: boolean;
    };
    const { onClick, tabIndex, onKeyDown, ...aRest } = anchorAll;
    const anchorClassName = disabled ? `${classes} disabled` : classes;

    const handleAnchorClick: React.MouseEventHandler<HTMLAnchorElement> = (e) => {
      if (disabled) {
        e.preventDefault();
        e.stopPropagation();
        return;
      }
      onClick?.(e);
    };

    const handleAnchorKeyDown: React.KeyboardEventHandler<HTMLAnchorElement> = (e) => {
      if (disabled && (e.key === 'Enter' || e.key === ' ')) {
        e.preventDefault();
        return;
      }
      onKeyDown?.(e);
    };

    return (
      <a
        {...aRest}
        className={anchorClassName}
        href={disabled ? undefined : href}
        aria-disabled={disabled || undefined}
        tabIndex={disabled ? -1 : tabIndex}
        onClick={handleAnchorClick}
        onKeyDown={handleAnchorKeyDown}
      >
        {content}
      </a>
    );
  }
  const { type, 'aria-label': ariaLabel, ...buttonProps } = rest as ButtonAsButton;
  // If no aria-label is provided, use label (if string) for accessibility
  const computedAriaLabel = ariaLabel || (typeof label === 'string' ? label : undefined);
  return (
    <button
      ref={ref}
      className={classes}
      {...(type ? { type } : {})}
      {...buttonProps}
      {...(computedAriaLabel ? { 'aria-label': computedAriaLabel } : {})}
      disabled={disabled}
    >
      {content}
    </button>
  );
});

Button.displayName = 'Button';

export default Button;
